import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { patch } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { CorrectLicenseRequest, UpdateLicenseResponse } from '../../../../shared/types/licenses';

import { getQueryKey as getRevisionLicenseQueryKey } from './useRevisionLicense';

const patchLicense = (
  dependencyRevisionLocator: string,
  revisionLicenseId: number,
  updateLicenseRequest: CorrectLicenseRequest,
) => {
  return patch<CorrectLicenseRequest, UpdateLicenseResponse>(
    `/api/v2/revisions/${nuc(dependencyRevisionLocator)}/correction/license/${revisionLicenseId}`,
    updateLicenseRequest,
  );
};

/**
 * Hook to correct a single license. Not intended for adding new licenses or removing licenses.
 * @param dependencyRevisionLocator The locator of the dependency revision to correct.
 * @param revisionLicenseId The `RevisionLicense of the revision license to correct.
 * @returns A mutation to correct a single license.
 */
export default function useCorrectLicense(dependencyRevisionLocator: string, revisionLicenseId: number) {
  return useToastedMutation({
    mutationFn: (updateLicenseRequest: CorrectLicenseRequest) =>
      patchLicense(dependencyRevisionLocator, revisionLicenseId, updateLicenseRequest),
    successToastMessage: 'License corrected successfully',
    errorToastMessage: 'Failed to correct license',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getRevisionLicenseQueryKey(dependencyRevisionLocator, revisionLicenseId),
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key.includes('dependencies') && key.includes(dependencyRevisionLocator);
        },
      });
    },
  });
}
